// AI-ASSISTED: generated with claude-code on 2026-06-10, reviewed by Yoann Hertienne
import useAsync from 'react-use/lib/useAsync';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

const REPO_ANNOTATION = 'azure.com/acr-repo';

// Per-env candidate repo names the card probes. Each env tries the bare
// `<repo>` first (works for components like `backstage` whose image name
// is identical in both envs) plus the Luminus per-env suffix variant
// `<repo>-b2c-euw-<env>` (works for components like my-luminus-web that
// split images by env). 404 → silently dropped; 200 → rendered.
const ENV_CANDIDATES: ReadonlyArray<{ env: 'nprd' | 'prd'; suffixes: string[] }> = [
  { env: 'nprd', suffixes: ['', '-b2c-euw-dev', '-b2c-euw-tst', '-b2c-euw-acc'] },
  { env: 'prd', suffixes: ['', '-b2c-euw-prd'] },
];

type AcrTag = {
  name: string;
  createdTime?: string;
  lastUpdateTime?: string;
};

export const parseTags = (payload: unknown): AcrTag[] => {
  if (!payload || typeof payload !== 'object') return [];
  const tags = (payload as Record<string, unknown>).tags;
  if (!Array.isArray(tags)) return [];
  return tags
    .filter((t): t is Record<string, unknown> => Boolean(t) && typeof t === 'object')
    .map(t => ({
      name: String(t.name ?? ''),
      createdTime: typeof t.createdTime === 'string' ? t.createdTime : undefined,
      lastUpdateTime:
        typeof t.lastUpdateTime === 'string' ? t.lastUpdateTime : undefined,
    }))
    .filter(t => t.name !== '');
};

/**
 * Builds the full probe list — every env × every candidate repo name.
 * The card fires each probe in parallel and keeps only the ones that
 * the backend confirms exist (200 OK with tags).
 */
export const buildCandidates = (
  repo: string,
): Array<{ env: 'nprd' | 'prd'; repo: string }> => {
  const out: Array<{ env: 'nprd' | 'prd'; repo: string }> = [];
  for (const { env, suffixes } of ENV_CANDIDATES) {
    for (const suffix of suffixes) {
      out.push({ env, repo: `${repo}${suffix}` });
    }
  }
  return out;
};

type Section = {
  env: 'nprd' | 'prd';
  repo: string;
  tags: AcrTag[];
};

const AcrCardBody = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const repo = entity.metadata.annotations?.[REPO_ANNOTATION];

  const { value, loading, error } = useAsync(async () => {
    if (!repo) return undefined;
    const baseUrl = await discoveryApi.getBaseUrl('acr');
    const candidates = buildCandidates(repo);
    const probes = candidates.map(async ({ env, repo: candidateRepo }) => {
      try {
        const url = `${baseUrl}/${env}/${candidateRepo}/_tags?orderby=timedesc&n=10`;
        const response = await fetchApi.fetch(url);
        if (!response.ok) {
          // 404 = repo doesn't exist in this env → silently drop.
          // 401/403 = scope-token missing the repo → also drop (degrades
          // to whichever envs/repos succeed).
          return undefined;
        }
        const tags = parseTags(await response.json());
        return { env, repo: candidateRepo, tags } as Section;
      } catch {
        return undefined;
      }
    });
    const sections = (await Promise.all(probes)).filter(
      (s): s is Section => Boolean(s),
    );
    return sections;
  }, [repo]);

  if (!repo) return null;
  if (error) throw error;

  const sections = value ?? [];

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Azure Container Registry</Typography>
          <Chip size="small" label={repo} />
        </Stack>
        {loading && (
          <Box sx={{ py: 1 }}>
            <CircularProgress size={20} aria-label="Loading ACR tags" />
          </Box>
        )}
        {!loading && sections.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            No matching ACR repositories found in nprd or prd.
          </Typography>
        )}
        {!loading &&
          (['nprd', 'prd'] as const).map(env => {
            const envSections = sections.filter(s => s.env === env);
            if (envSections.length === 0) return null;
            return (
              <Box key={env} sx={{ pt: 1 }}>
                <Typography variant="subtitle1">{env.toUpperCase()}</Typography>
                {envSections.map(section => (
                  <Box key={`${section.env}-${section.repo}`} sx={{ pl: 1, pt: 0.5 }}>
                    <Typography variant="subtitle2">{section.repo}</Typography>
                    {section.tags.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No tags found.
                      </Typography>
                    )}
                    {section.tags.length > 0 && (
                      <List dense>
                        {section.tags.slice(0, 10).map(tag => (
                          <ListItem key={tag.name} disableGutters>
                            <ListItemText
                              primary={tag.name}
                              secondary={
                                tag.lastUpdateTime
                                  ? `updated ${tag.lastUpdateTime.slice(0, 10)}`
                                  : undefined
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                ))}
              </Box>
            );
          })}
      </CardContent>
    </Card>
  );
};

export const AcrCard = () => (
  <TileBoundary tool="ACR">
    <AcrCardBody />
  </TileBoundary>
);
